import { Dispatch, SetStateAction, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports - types
import warningImage from 'assets/images/warning-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import { IFilteredGymTrainerList } from 'types/GymType'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import { ActionArea, CancelButton, ConfirmButton } from 'components/home/on-campus-services/gym/gym-list/styled'
import ScheduleSidebar from 'components/home/on-campus-services/gym/gym-list/trainer/reservation-scheduler/ScheduleSidebar'
import Scheduler from 'components/home/on-campus-services/gym/gym-list/trainer/reservation-scheduler/Scheduler'
import ReservationModalHeader from 'components/home/on-campus-services/gym/gym-list/trainer/reservation-scheduler/ReservationModalHeader'
import useGetGymTrainerReservationTable from 'components/home/on-campus-services/gym/hooks/use-get-gym-trainer-res-table'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  filterDate: Date
  selectedTrainer: IFilteredGymTrainerList
  trainers: IFilteredGymTrainerList[] | []
  setSelectedTrainer: Dispatch<SetStateAction<IFilteredGymTrainerList | undefined>>
  open: boolean
  closeModal: () => void
  cancelReservationModal: () => void
  openModal: () => void
  showNotification: () => void
  selectedDateStr?: string
}

export default function GymTrainerResModal({
  filterDate: dateFromList,
  trainers,
  selectedTrainer,
  setSelectedTrainer,
  open,
  closeModal,
  openModal,
  showNotification,
  cancelReservationModal,
  selectedDateStr,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Gym PT - Trainer reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const [diffDayNotification, setDiffDayNotification] = useState(false)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  const isSelectedDifferentDate = moment(filterDate).isSame(dateFromList, 'day')

  /* GET Selected Gym Trainers Reservation Table List | Custom Hook  */
  const salonLocationId = selectedTrainer.gymSaloon.floor.locationId
  const salonId = selectedTrainer.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedAppointment,
    setSelectedAppointment,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetGymTrainerReservationTable(salonLocationId, salonId, dateFromList, true)

  /* Open Reservation Sidebar */
  const openReservationSidebar = () => {
    if (isSelectedDifferentDate) {
      setSidebarVisible(true)
    } else {
      setDiffDayNotification(true)
    }
    closeModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    showNotification()
    setSidebarVisible(false)
    closeModal()
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    cancelReservationModal()
    setFilterDate(dateFromList)
    setSelectedAppointment(undefined)
    setTableWeek(lastFilteredDate)
  }

  /* Different Day Reservation - Notification Dialog Methods */
  const handleAcceptDiffDayReservation = () => {
    setDiffDayNotification(false)
    setSidebarVisible(true)
    closeModal()
  }
  const handleRejectDiffDayReservation = () => {
    setDiffDayNotification(false)
    openModal()
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        closable={true}
        focusOnShow={false}
        header={<span>{`${t('gym-res-modal.gym-reservation')} / ${t('gym-res-modal.trainer-res')}`} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openReservationSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedAppointment}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          selectedDateStr={selectedDateStr}
          trainer={selectedTrainer}
          trainers={trainers}
          setSelectedTrainer={setSelectedTrainer}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedAppointment && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          trainerInfo={selectedTrainer}
          appointmentInfo={selectedAppointment}
        />
      )}

      <NotificationDialog
        display={diffDayNotification}
        imgSrc={warningImage}
        goHomeButton={true}
        extraButton={true}
        title={t('overlaypanel-texts.reservation-day-changed')}
        content={t('overlaypanel-texts.reservation-day-change-text')}
        firstButtonLabel={t('buttons.yes')}
        secondButtonLabel={t('buttons.no')}
        onHide={handleRejectDiffDayReservation}
        onSubmit={handleAcceptDiffDayReservation}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}
